import { ConsoleContinue } from "components/platform/companyConsole/ConsoleState";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Typography } from "@mui/material";
import { titleCase } from "title-case";
import { DataTable } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import { GET_CARDS } from "graphql/card";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { useCompanyConnection } from "hooks/useCompanyConnection";
import { useAdminTools } from "hooks/useAdminTools";
import { DEFAULT_PAGINATION_SIZE } from "components/common/Constants";
import { Inline } from "components/layout/reusableLayouts";
import CardBalanceBar, {
  CARD_BALANCE_BAR_WIDTH,
} from "components/common/cards/CardBalanceBar";
import CardRestrictionsCell from "components/common/cards/CardRestrictionsCell";
import CardStatusChip from "components/common/cards/CardStatusChip";
import { cardRequestTypeLabel } from "utils/cards/cardRequestTypeLabel";
import type { CardFilterCriteria } from "utils/cards/cardFilters";
import { cardRowData, EMPTY_CELL } from "utils/cards/cardRowData";
import type { CardRowData, CardRowNode } from "utils/cards/cardRowData";
import UserAvatar from "components/common/users/UserAvatar";

const extractCards = (data: {
  cards?: { edges?: { node: CardRowNode }[] };
}): CardRowData[] =>
  (data.cards?.edges ?? []).map(({ node }) => cardRowData(node));

interface CardsTableProps {
  filterData: CardFilterCriteria;
  selectedCompanyId?: string;
  financialAccountId?: string;
  issuer?: string;
  currency?: string;
  /** Only the viewer's own cards, whatever their role. */
  mine?: boolean;
  handleOpenCardDetails: (cardId: string) => void;
  refreshCards?: number;
}

const CardsTable: React.FC<CardsTableProps> = ({
  filterData,
  selectedCompanyId,
  financialAccountId,
  issuer,
  currency,
  mine = false,
  handleOpenCardDetails,
  refreshCards,
}) => {
  const { t } = useTranslation();
  const adminTools = useAdminTools();

  const variables = useMemo(
    () => ({
      first: DEFAULT_PAGINATION_SIZE,
      // The server resolves `mine` to the viewer, so /my never sends, or trusts, a client-side id.
      ...(mine ? { mine: true } : { userId: filterData.userId ?? undefined }),
      cardStatus: filterData.cardStatus.length
        ? filterData.cardStatus
        : undefined,
      cardType: filterData.cardType.length ? filterData.cardType : undefined,
      searchTerm: filterData.searchTerm,
    }),
    [filterData, mine],
  );

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_CARDS,
      initialVariables: variables,
      extractData: extractCards,
      refetchIndex: refreshCards,
      skip: !!selectedCompanyId,
    });

  const scoped = useCompanyConnection<CardRowNode>({
    query: GET_CARDS,
    variables: {
      ...variables,
      selectedCompanyId,
      financialAccountId,
      issuer,
      currency,
      console: true,
    },
    field: "cards",
    skip: !selectedCompanyId,
    refresh: refreshCards,
  });

  const columns: DataColumn<CardRowData>[] = useMemo(
    () => [
      ...(adminTools
        ? [
            {
              id: "userName",
              header: t("Name"),
              width: "20%",
              cell: (card: CardRowData) => (
                <Inline gap="var(--space-3)" wrap={false}>
                  <UserAvatar size="sm" user={card.user} />
                  <Typography variant="body1">{card.userName}</Typography>
                </Inline>
              ),
            },
          ]
        : []),
      ...(selectedCompanyId
        ? [
            {
              id: "program",
              header: t("Account / issuer"),
              cell: (card: CardRowData) => {
                const program = scoped.rows.find(
                  (row) => row.id === card.id,
                )?.consoleProgram;
                return {
                  content: program?.name || t("Unavailable"),
                  caption: program?.issuer,
                };
              },
            },
          ]
        : []),
      {
        id: "cardNumber",
        header: t("Card Number"),
        width: adminTools ? "18%" : "25%",
        maxWidth: 200,
        cell: (card) => ({
          content: card.cardNumber,
          caption: t(titleCase(card.type.toLowerCase())),
        }),
      },
      {
        id: "cardStatus",
        header: t("Card Status"),
        align: "center",
        width: adminTools ? "12%" : "14%",
        cell: (card) => {
          const requestLabel = card.isPendingRequest
            ? (cardRequestTypeLabel(card.pendingRequestType ?? undefined, t) ??
              t("Pending"))
            : null;
          return {
            content: (
              <CardStatusChip
                status={card.cardStatus}
                pendingTerms={card.pendingTerms}
              />
            ),
            caption: requestLabel
              ? t("PendingRequestCaption", { type: requestLabel })
              : undefined,
          };
        },
      },
      {
        id: "expiration",
        header: t("Expiration"),
        width: adminTools ? "12%" : "14%",
        cell: (card) => ({
          content: card.expiration ?? EMPTY_CELL,
          muted: !card.expiration,
          caption: card.cancelation
            ? t("CancelsOn", { date: card.cancelation })
            : undefined,
        }),
      },
      {
        id: "availableBalance",
        header: t("Available Balance"),
        width: CARD_BALANCE_BAR_WIDTH,
        cell: (card) => (
          <CardBalanceBar
            spent={card.spent}
            limit={card.limit}
            status={card.cardStatus}
            limitType={card.limitType}
          />
        ),
      },
      {
        id: "merchants",
        header: t("Merchants / Categories"),
        width: adminTools ? "18%" : "27%",
        cell: (card) => (
          <CardRestrictionsCell items={card.combinedCategoryAndMerchant} />
        ),
      },
    ],
    [t, adminTools, selectedCompanyId, scoped.rows],
  );

  return (
    <Box ref={scoped.viewportRef} sx={{ height: "100%", minHeight: 0 }}>
      {selectedCompanyId && scoped.error && (
        <Alert severity="error">
          {t("Cards are unavailable or access is denied")}
        </Alert>
      )}
      <ConsoleContinue paused={scoped.paused} loadMore={scoped.loadMore} />
      <DataTable<CardRowData>
        columns={columns}
        rows={selectedCompanyId ? scoped.rows.map(cardRowData) : dataArray}
        getRowKey={(card) => card.id}
        loading={selectedCompanyId ? scoped.loading : displayLoading}
        isFetchingMore={
          selectedCompanyId ? scoped.isFetchingMore : isInfiniteScrollFetching
        }
        onRowClick={(card) => handleOpenCardDetails(card.id)}
        onScroll={selectedCompanyId ? scoped.handleScroll : handleScroll}
        emptyState={t("No cards found")}
        loadingMoreText={t("Loading more cards")}
        stickyHeader
      />
    </Box>
  );
};

export default CardsTable;
